import React, { useId } from "react";
import {
  RoomImage,
  PictureProperty,
} from "@/services/private-office-service/types";
import { cn } from "@/lib/utils";
import { Check, ImageIcon } from "lucide-react";

interface ImageSelectionGalleryProps {
  images: RoomImage[];
  selectedProperty: PictureProperty | null;
  onSelect: (property: PictureProperty) => void;
  className?: string;
}

export const ImageSelectionGallery: React.FC<ImageSelectionGalleryProps> = ({
  images,
  selectedProperty,
  onSelect,
  className,
}) => {
  // Scoped per instance: a room with several pending image requests renders one
  // gallery per request, and a shared name would fuse them into a single radio
  // group where selecting in one clears the other.
  const groupName = useId();

  return (
    <div className={cn("space-y-4", className)}>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {images.map((image) => {
          const isSelected = selectedProperty === image.property;

          return (
            <div
              key={image.property}
              className={cn(
                "relative aspect-video cursor-pointer rounded-xl overflow-hidden transition-all duration-300 group",
                "hover:scale-105 hover:shadow-lg",
                isSelected
                  ? "ring-3 ring-yellow-400 shadow-lg scale-105"
                  : "ring-1 ring-gray-200 hover:ring-gray-300",
              )}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(image.property);
              }}
            >
              {/* Image or placeholder */}
              {image.url ? (
                <img
                  src={image.url}
                  alt={image.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                  <div className="text-center">
                    <ImageIcon className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <span className="text-xs text-gray-500 font-medium">
                      Vazia
                    </span>
                  </div>
                </div>
              )}

              {/* Overlay with selection indicator */}
              <div
                className={cn(
                  "absolute inset-0 transition-all duration-300",
                  isSelected
                    ? "bg-yellow-400/20"
                    : "bg-black/0 group-hover:bg-black/10",
                )}
              >
                {/* Selection checkmark */}
                {isSelected && (
                  <div className="absolute top-3 right-3 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg">
                    <Check className="w-4 h-4 text-white" strokeWidth={2.5} />
                  </div>
                )}

                {/* Image label */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                  <div className="text-white">
                    <p className="text-sm font-semibold">{image.name}</p>
                    {!image.isEmpty && (
                      <p className="text-xs text-white/80">
                        Substitui a imagem atual
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Radio button for accessibility */}
              <input
                type="radio"
                name={groupName}
                checked={isSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  onSelect(image.property);
                }}
                className="sr-only"
                aria-label={`Selecionar ${image.name}`}
              />
            </div>
          );
        })}
      </div>

      {/* Selection summary */}
      <div className="text-center">
        {selectedProperty ? (
          <p className="text-sm text-gray-600">
            <span className="font-medium text-yellow-600">Selecionado:</span>{" "}
            {images.find((img) => img.property === selectedProperty)?.name}
          </p>
        ) : (
          <p className="text-sm text-gray-500">
            Clique em uma imagem para selecioná-la como substituição
          </p>
        )}
      </div>
    </div>
  );
};
